import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Tabs,
  Tab,
  Checkbox,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Skeleton,
  useTheme,
  Button,
  IconButton,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ReferenceLine,
} from 'recharts';
import { formatCurrency, formatNumber } from './utils/dataTransform';
import { useTariff } from './tariff/TariffContext';
import monthlyPerformanceService from './services/monthlyPerformanceService';
import useCapexRates from './hooks/useCapexRates';
import BudgetTab from './components/BudgetTab';
import OpExTable from './data-display/OpExTable';
import { convertGasTariffForSolver, calculateTieredGasCost } from './utils/gasTariffConverter';
import { MACRS_SCHEDULES } from './hooks/useCommercialAssumptions';

// Enhanced financial calculator for multi-year proforma analysis
// Exported for use in ResultsSummary and other components
export class FinancialCalculator {
  constructor(reoptData, customAssumptions = {}, monthlyPerformanceData = null, overrideCapitalCost = null, opexCalculations = null, overrideItcAmount = null, escalationRates = null, gasTariff = null, commercialAssumptions = null) {
    this.reoptData = reoptData;
    this.overrideCapitalCost = overrideCapitalCost;
    this.overrideItcAmount = overrideItcAmount;
    this.opexCalculations = opexCalculations; // Custom OpEx from useBudget hook
    this.gasTariff = gasTariff; // Gas tariff for tiered cost calculation

    // Normalize outputs structure - REopt can return data in two formats:
    // 1. Nested: outputs.Scenario.Site.*
    // 2. Flat: outputs.*
    const rawOutputs = reoptData?.outputs || {};
    if (rawOutputs.Scenario && rawOutputs.Scenario.Site) {
      // Nested structure - flatten it
      this.outputs = rawOutputs.Scenario.Site;
    } else {
      // Already flat structure
      this.outputs = rawOutputs;
    }

    this.inputs = reoptData?.inputs || {};
    this.monthlyPerformanceData = monthlyPerformanceData;

    // Escalation rates from Commercial Assumptions tab (or defaults)
    // escalationRates passed in are already in decimal form from commercialAssumptions.calculatorInputs
    this.escalationRates = escalationRates || {
      energy: 0.03,     // fraction per year
      demand: 0.03,     // fraction per year
      naturalGas: 0.025, // fraction per year
      labor: 0.025      // fraction per year
    };

    // Use commercialAssumptions if provided, otherwise fall back to defaults
    const effectiveAssumptions = commercialAssumptions || {};

    // Financial assumptions for NPV, payback, and lifecycle calculations
    this.assumptions = {
      analysisYears: effectiveAssumptions.analysisYears || 20,
      discountRate: effectiveAssumptions.discountRate || 0.08,
      degradationRate: effectiveAssumptions.degradationRate || 0.005, // PV degradation per year
      taxRate: effectiveAssumptions.taxRate || 0.25,
      ...customAssumptions
    };

    // Tax incentive settings (MACRS, ITC treatment) — all values already in decimal
    const ti = effectiveAssumptions.taxIncentives || {};
    this.taxIncentives = {
      includeMacrs: ti.includeMacrs ?? false,
      macrsSchedule: ti.macrsSchedule ?? '5-year',
      itcTreatment: ti.itcTreatment ?? 'upfront_reduction',
      itcTransferDiscount: ti.itcTransferDiscount ?? 0.10,   // decimal (0.10 = 10%)
      macrsBasisReduction: ti.macrsBasisReduction ?? 0.50,    // decimal (0.50 = 50%)
    };

    // Escalation rates are already in decimal form (e.g., 0.03 for 3%)
    this.escalationRatesDecimal = this.escalationRates;

    this.systemData = this.extractSystemData();
    this.energyData = this.extractEnergyData();
  }

  extractSystemData() {
    const pv = this.outputs.PV || {};
    const storage = this.outputs.ElectricStorage || {};
    const generator = this.outputs.Generator || {};
    const financial = this.outputs.Financial || {};

    // Get total system costs from Financial section
    const totalCapitalCost = financial.initial_capital_costs || 0;
    const totalCapitalCostAfterIncentives = financial.initial_capital_costs_after_incentives || totalCapitalCost;

    // Storage has its own cost field, use it
    const storageCost = storage.initial_capital_costs_after_incentives || storage.initial_capital_costs || storage.initial_capital_cost || 0;

    // Calculate PV and Generator costs as proportional shares of remaining costs
    const remainingCost = Math.max(0, totalCapitalCostAfterIncentives - storageCost);
    const pvSize = pv.size_kw || 0;
    const genSize = generator.size_kw || 0;
    const totalNonStorageSize = pvSize + genSize;

    const pvCost = totalNonStorageSize > 0 ? remainingCost * (pvSize / totalNonStorageSize) : 0;
    const generatorCost = totalNonStorageSize > 0 ? remainingCost * (genSize / totalNonStorageSize) : 0;

    // Get prescribed sizes from inputs
    const pvInput = this.inputs.PV || {};
    const storageInput = this.inputs.ElectricStorage || {};
    const generatorInput = this.inputs.Generator || {};

    const systemData = {
      pvSize: pvSize,
      pvPrescribed: pvInput.size_kw || pvInput.max_kw || pvInput.min_kw || pvSize,
      pvCost: pvCost,
      pvAnnualProduction: pv.annual_energy_produced_kwh || 0,
      storageSize: storage.size_kw || 0,
      storagePrescribed: storageInput.size_kw || storageInput.max_kw || storageInput.min_kw || storage.size_kw || 0,
      storageCost: storageCost,
      storageCapacity: storage.size_kwh || 0,
      storageCapacityPrescribed: storageInput.size_kwh || storageInput.max_kwh || storageInput.min_kwh || storage.size_kwh || 0,
      generatorSize: genSize,
      generatorPrescribed: generatorInput.size_kw || generatorInput.max_kw || generatorInput.min_kw || genSize,
      generatorCost: generatorCost,
    };

    console.log('System sizing data:', {
      pvSize,
      pvPrescribed: systemData.pvPrescribed,
      storageSize: systemData.storageSize,
      storagePrescribed: systemData.storagePrescribed,
      generatorSize: genSize,
      generatorPrescribed: systemData.generatorPrescribed,
    });

    console.log('Financial costs:', {
      totalCapitalCost,
      totalCapitalCostAfterIncentives,
      storageCost,
      remainingCost,
      pvCost,
      generatorCost
    });
    console.log('System Data with costs:', systemData);
    return systemData;
  }

  extractEnergyData() {
    const financial = this.outputs.Financial || {};
    const electricLoad = this.outputs.ElectricLoad || {};
    const site = this.outputs.Site || {};

    // Calculate total load from hourly data if annual_kwh is not available
    let totalLoad = electricLoad.annual_kwh || site.annual_kwh || 0;
    if (totalLoad === 0 && electricLoad.load_series_kw && Array.isArray(electricLoad.load_series_kw)) {
      totalLoad = electricLoad.load_series_kw.reduce((sum, val) => sum + (val || 0), 0);
    }

    // Use monthly performance data if available (more accurate than REopt estimates)
    let yearOneUtilityCostBau = 0;
    let yearOneUtilityCostProject = 0;
    let lccBau = 0;
    let bauDataSource = 'reopt';

    console.log('📊 [FinancialCalculator] Checking monthly performance data:', {
      hasMonthlyData: !!this.monthlyPerformanceData,
      bauMonthlyCount: this.monthlyPerformanceData?.bau_monthly_costs?.length,
      projectMonthlyCount: this.monthlyPerformanceData?.project_monthly_costs?.length
    });

    if (this.monthlyPerformanceData) {
      const { bau_monthly_costs = [], project_monthly_costs = [], tariff_data_source = 'unknown' } = this.monthlyPerformanceData;

      // Use our calculated costs (more accurate)
      if (bau_monthly_costs.length > 0) {
        yearOneUtilityCostBau = bau_monthly_costs.reduce((sum, month) => sum + (month.total_cost || 0), 0);
        console.log('📊 [FinancialCalculator] Using monthly BAU costs:', {
          monthlyCount: bau_monthly_costs.length,
          yearOneUtilityCostBau,
          sample: bau_monthly_costs[0]
        });

        // Map backend tariff data sources to valid frontend data sources
        if (tariff_data_source === 'urdb_actual' || tariff_data_source === 'urdb_label_fetch_failback') {
          bauDataSource = 'backend_calculator';
        } else {
          bauDataSource = 'reopt'; // Default to reopt for any other source
        }

        // Estimate lifecycle costs with energy escalation rate
        lccBau = 0;
        for (let year = 1; year <= this.assumptions.analysisYears; year++) {
          lccBau += yearOneUtilityCostBau * Math.pow(1 + this.escalationRatesDecimal.energy, year - 1);
        }
      }

      if (project_monthly_costs.length > 0) {
        yearOneUtilityCostProject = project_monthly_costs.reduce((sum, month) => sum + (month.total_cost || 0), 0);
        console.log('📊 [FinancialCalculator] Using monthly project costs:', {
          monthlyCount: project_monthly_costs.length,
          yearOneUtilityCostProject,
          sample: project_monthly_costs[0]
        });
      }
    }

    // NO FALLBACK: Monthly performance data is required for accurate BAU calculations
    // If no monthly data available, warn but don't use incorrect REopt values
    if (yearOneUtilityCostBau === 0 && !this.monthlyPerformanceData) {
      console.warn('⚠️ [FinancialCalculator] No monthly performance data - BAU costs will be 0. Waiting for data...');
    }

    return {
      totalLoad,
      yearOneUtilityCost: yearOneUtilityCostProject,
      yearOneOmCosts: financial.year_one_om_costs_before_tax || 0,
      // BAU baseline data - from monthly performance data ONLY
      yearOneUtilityCostBau,
      yearOneOmCostsBau: financial.year_one_om_costs_before_tax_bau || 0,
      lccBau,
      bauDataSource: this.monthlyPerformanceData ? 'monthly_performance' : 'none',
      // Add validation data if available
      validation: this.monthlyPerformanceData?.validation,
      // CA DER charges from monthly performance data
      yearOneDerCharges: this.extractDerCharges(),
    };
  }

  extractDerCharges() {
    // Extract CA DER charges from monthly performance data
    // Returns object with separated standby and departing load costs
    if (this.monthlyPerformanceData?.monthly_der_charges?.length > 0) {
      const standbyCharges = this.monthlyPerformanceData.monthly_der_charges.reduce(
        (sum, month) => sum + (month.standby_cost || 0), 0
      );
      const departingLoadCharges = this.monthlyPerformanceData.monthly_der_charges.reduce(
        (sum, month) => sum + (month.departing_load_cost || 0), 0
      );
      const yearOneDerCharges = standbyCharges + departingLoadCharges;

      console.log('📊 [FinancialCalculator] CA DER Charges (separated):', {
        monthlyCount: this.monthlyPerformanceData.monthly_der_charges.length,
        standbyCharges,
        departingLoadCharges,
        yearOneDerCharges,
        sample: this.monthlyPerformanceData.monthly_der_charges[0]
      });

      // Return total for backwards compatibility, but also provide breakdown
      // Standby charges are shown separately in UI, departing load rolled into utility
      return {
        total: yearOneDerCharges,
        standby: standbyCharges,
        departingLoad: departingLoadCharges
      };
    }
    return { total: 0, standby: 0, departingLoad: 0 };
  }

  calculateFuelCosts() {
    // PRIORITY: Use pre-calculated fuel costs from monthly performance data (backend calculation)
    // This includes proper fuel efficiency, tariff rates, and hourly dispatch from the solver
    let totalFuelCostFromBackend = 0;
    let generatorAnnualProduction = 0;

    if (this.monthlyPerformanceData?.asset_performance) {
      this.monthlyPerformanceData.asset_performance.forEach(month => {
        totalFuelCostFromBackend += month.generator_fuel_cost || 0;
        generatorAnnualProduction += month.generator_to_load_kwh || 0;
      });
    }

    console.log('⛽ [calculateFuelCosts] Using backend-calculated fuel costs:', {
      hasMonthlyData: !!this.monthlyPerformanceData,
      assetPerformanceLength: this.monthlyPerformanceData?.asset_performance?.length,
      totalFuelCostFromBackend: `$${totalFuelCostFromBackend.toFixed(2)}`,
      generatorAnnualProduction: `${generatorAnnualProduction.toFixed(0)} kWh`,
    });

    // If backend has calculated fuel costs, use them directly
    if (totalFuelCostFromBackend > 0) {
      console.log('⛽ [calculateFuelCosts] Using backend fuel cost: $' + totalFuelCostFromBackend.toFixed(2));
      return {
        dieselFuelCost: totalFuelCostFromBackend, // Backend doesn't split by fuel type
        naturalGasFuelCost: 0,
        totalFuelCost: totalFuelCostFromBackend,
        generatorProduction: generatorAnnualProduction,
        source: 'backend'
      };
    }

    // If no generator production, no fuel costs
    if (generatorAnnualProduction === 0) {
      console.log('⛽ [calculateFuelCosts] No generator production - returning 0 fuel costs');
      return {
        dieselFuelCost: 0,
        naturalGasFuelCost: 0,
        totalFuelCost: 0,
        generatorProduction: 0,
      };
    }

    // FALLBACK: Calculate fuel costs on frontend if backend didn't provide them
    console.log('⛽ [calculateFuelCosts] Backend fuel cost is 0, using frontend calculation');

    // Get fuel prices and efficiency from custom OpEx (if provided)
    const fuelConfig = this.opexCalculations?.fuel || {};
    const dieselPrice = fuelConfig.dieselPrice || 3.50; // $/gallon
    const naturalGasPrice = fuelConfig.naturalGasPrice || 0.50; // $/therm
    const dieselEfficiency = fuelConfig.dieselEfficiency || 13.5; // kWh/gallon
    const naturalGasEfficiency = fuelConfig.naturalGasEfficiency || 11.0; // kWh/therm

    // Determine fuel type from inputs
    const generatorInput = this.inputs.Generator || {};
    const chpInput = this.inputs.CHP || {};

    let dieselFuelCost = 0;
    let naturalGasFuelCost = 0;

    // Check if we have diesel generator (Generator object exists)
    if (generatorInput.max_kw || generatorInput.size_kw) {
      // Diesel generator: calculate fuel consumption
      const dieselGallons = generatorAnnualProduction / dieselEfficiency;
      dieselFuelCost = dieselGallons * dieselPrice;
    }

    // Check if we have natural gas generator (CHP object exists)
    if (chpInput.max_kw || chpInput.size_kw) {
      // Natural gas generator: calculate fuel consumption based on kWh production
      const naturalGasTherms = generatorAnnualProduction / naturalGasEfficiency;
      const naturalGasMmbtu = naturalGasTherms / 10; // Convert therms to MMBtu

      // Use tiered gas tariff if available
      if (this.gasTariff && this.gasTariff.tariff_type === 'natural_gas') {
        const convertedTariff = convertGasTariffForSolver(this.gasTariff);
        naturalGasFuelCost = calculateTieredGasCost(naturalGasMmbtu, convertedTariff);
        console.log('⛽ [FinancialCalculator] Using tiered gas tariff:', {
          generatorProduction: `${generatorAnnualProduction.toFixed(0)} kWh`,
          naturalGasTherms: `${naturalGasTherms.toFixed(1)} therms`,
          naturalGasMmbtu: `${naturalGasMmbtu.toFixed(2)} MMBtu`,
          tariffName: this.gasTariff.tariff_name,
          naturalGasFuelCost: `$${naturalGasFuelCost.toFixed(2)}`
        });
      } else {
        // Fall back to simple flat rate calculation
        naturalGasFuelCost = naturalGasTherms * naturalGasPrice;
        console.log('⛽ [FinancialCalculator] Using flat gas rate:', {
          generatorProduction: `${generatorAnnualProduction.toFixed(0)} kWh`,
          naturalGasTherms: `${naturalGasTherms.toFixed(1)} therms`,
          flatRate: `$${naturalGasPrice}/therm`,
          naturalGasFuelCost: `$${naturalGasFuelCost.toFixed(2)}`
        });
      }
    }

    const totalFuelCost = dieselFuelCost + naturalGasFuelCost;

    console.log('⛽ [FinancialCalculator] Fuel Costs (frontend fallback):', {
      generatorProduction: generatorAnnualProduction.toFixed(0) + ' kWh',
      dieselFuelCost: dieselFuelCost > 0 ? `$${dieselFuelCost.toFixed(2)}` : '$0',
      naturalGasFuelCost: naturalGasFuelCost > 0 ? `$${naturalGasFuelCost.toFixed(2)}` : '$0',
      totalFuelCost: `$${totalFuelCost.toFixed(2)}`,
      fuelPrices: {
        diesel: `$${dieselPrice}/gal`,
        naturalGas: this.gasTariff ? 'tiered tariff' : `$${naturalGasPrice}/therm`
      }
    });

    return {
      dieselFuelCost,
      naturalGasFuelCost,
      totalFuelCost,
      generatorProduction: generatorAnnualProduction,
      source: 'frontend'
    };
  }

  calculateSystemCosts() {
    const financial = this.outputs.Financial || {};

    // CRITICAL: Budget tab is the ONLY source of capital costs
    // We should NEVER use solver's financial.initial_capital_costs
    if (this.overrideCapitalCost === null) {
      console.error('❌ [FinancialCalculator] MISSING BUDGET CAPEX - Budget tab must provide capital costs');
      throw new Error('Budget capital costs required - solver financial costs should not be used');
    }

    const totalCapitalCost = this.overrideCapitalCost;
    // ITC treatment determines how the credit affects capital cost vs cashflows:
    // - upfront_reduction: traditional model, reduces initial outlay
    // - year_one_credit / transfer: ITC flows as a cashflow, initial outlay = full CapEx
    const itcReducesUpfront = this.taxIncentives.itcTreatment === 'upfront_reduction';
    const capitalCostAfterIncentives = itcReducesUpfront
      ? this.overrideCapitalCost - (this.overrideItcAmount || 0)
      : this.overrideCapitalCost;

    // Use custom OpEx if provided (from useBudget hook), otherwise fall back to REopt's O&M costs
    const omCosts = this.opexCalculations?.totalAnnualOM ?? (financial.year_one_om_costs_before_tax || 0);

    // Calculate fuel costs based on generator runtime
    const fuelCosts = this.calculateFuelCosts();

    console.log('📊 [FinancialCalculator] Capital Costs (Budget Only):', {
      totalCapitalCost: totalCapitalCost,
      itcAmount: this.overrideItcAmount,
      capitalCostAfterIncentives: capitalCostAfterIncentives,
      solverCostIgnored: financial.initial_capital_costs || 'N/A'
    });

    console.log('📊 [FinancialCalculator] O&M Costs:', {
      customOpEx: this.opexCalculations?.totalAnnualOM,
      opexBreakdown: this.opexCalculations ? {
        pvOM: this.opexCalculations.pvOM,
        batteryOM: this.opexCalculations.batteryOM,
        generatorOM: this.opexCalculations.generatorOM,
        fixedAnnualOM: this.opexCalculations.fixedAnnualOM
      } : 'NOT PROVIDED',
      reoptOM: financial.year_one_om_costs_before_tax,
      usingCustom: this.opexCalculations?.totalAnnualOM !== undefined,
      finalOM: omCosts,
      fuelCosts: fuelCosts.totalFuelCost
    });

    // CRITICAL DEBUG: Verify Budget tab values are being used
    console.log('💰 [FinancialCalculator] BUDGET TAB DATA CHECK:', {
      overrideCapitalCost: this.overrideCapitalCost,
      overrideItcAmount: this.overrideItcAmount,
      netCapitalCost: capitalCostAfterIncentives,
      opexCalculations: this.opexCalculations,
      annualOmCosts: omCosts,
      fuelCosts: fuelCosts.totalFuelCost
    });

    return {
      totalCapitalCost,
      capitalCostAfterIncentives,
      annualOmCosts: omCosts,
      annualFuelCosts: fuelCosts.totalFuelCost,
      fuelCostDetails: fuelCosts,
      netCapitalCost: capitalCostAfterIncentives,
    };
  }

  calculateProjectCashflows() {
    const systemCosts = this.calculateSystemCosts();
    const { pvAnnualProduction } = this.systemData;
    const { yearOneUtilityCostBau, yearOneUtilityCost, yearOneDerCharges } = this.energyData;

    // Extract DER charges breakdown (yearOneDerCharges is now an object with total, standby, departingLoad)
    const derChargesBreakdown = typeof yearOneDerCharges === 'object'
      ? yearOneDerCharges
      : { total: yearOneDerCharges || 0, standby: 0, departingLoad: yearOneDerCharges || 0 };
    const yearOneStandbyCharges = derChargesBreakdown.standby;
    const yearOneDepartingLoadCharges = derChargesBreakdown.departingLoad;

    // --- MACRS Depreciation Setup ---
    const { includeMacrs, macrsSchedule, itcTreatment, itcTransferDiscount, macrsBasisReduction } = this.taxIncentives;
    const taxRate = this.assumptions.taxRate;
    const itcAmount = this.overrideItcAmount || 0;

    let macrsDepreciationSchedule = [];
    let depreciableBasis = 0;
    if (includeMacrs && systemCosts.totalCapitalCost > 0) {
      // Depreciable basis = total CapEx minus ITC basis reduction (IRC Section 50(c))
      depreciableBasis = systemCosts.totalCapitalCost - (itcAmount * macrsBasisReduction);
      depreciableBasis = Math.max(0, depreciableBasis);
      macrsDepreciationSchedule = MACRS_SCHEDULES[macrsSchedule] || MACRS_SCHEDULES['5-year'];
    }

    // --- ITC Cashflow Setup ---
    // When ITC is NOT modeled as upfront reduction, it appears as a cashflow
    let itcCashflowYear1 = 0;
    if (itcAmount > 0 && itcTreatment !== 'upfront_reduction') {
      if (itcTreatment === 'year_one_credit') {
        itcCashflowYear1 = itcAmount;
      } else if (itcTreatment === 'transfer') {
        // Transfer/sale: receive ITC value minus the buyer's discount
        itcCashflowYear1 = itcAmount * (1 - itcTransferDiscount);
      }
    }

    // --- PV coverage ratio for degradation impact ---
    // The fraction of load served by PV determines how much degradation affects grid purchases
    const pvCoverageRatio = (pvAnnualProduction > 0 && yearOneUtilityCostBau > 0 && yearOneUtilityCost > 0)
      ? Math.min(1, (yearOneUtilityCostBau - yearOneUtilityCost) / yearOneUtilityCostBau)
      : 0;

    const cashflows = [];

    for (let year = 1; year <= this.assumptions.analysisYears; year++) {
      // PV degradation over time
      const pvDegradationFactor = Math.pow(1 - this.assumptions.degradationRate, year - 1);
      const adjustedPvProduction = pvAnnualProduction * pvDegradationFactor;

      // BAU utility cost (escalated over time) - always same regardless of degradation
      const bauUtilityCost = yearOneUtilityCostBau * Math.pow(1 + this.escalationRatesDecimal.energy, year - 1);

      // Project utility cost
      let projectUtilityCost;
      if (yearOneUtilityCost === 0) {
        // Off-grid system - no utility costs regardless of degradation
        projectUtilityCost = 0;
      } else {
        // Grid-connected: degradation means less PV offset, more grid purchases
        // Additional cost = BAU rate × fraction of lost PV production relative to total load
        const degradationImpact = pvAnnualProduction > 0
          ? (pvAnnualProduction - adjustedPvProduction) / pvAnnualProduction
          : 0;
        // Scale by PV coverage ratio: only the portion of load served by PV is affected
        const additionalUtilityCost = yearOneUtilityCostBau * degradationImpact * pvCoverageRatio;
        projectUtilityCost = (yearOneUtilityCost + additionalUtilityCost) * Math.pow(1 + this.escalationRatesDecimal.energy, year - 1);
      }

      // Escalated O&M costs (labor escalation rate)
      const escalatedOmCost = systemCosts.annualOmCosts * Math.pow(1 + this.escalationRatesDecimal.labor, year - 1);

      // Escalated fuel costs (natural gas escalation rate)
      const escalatedFuelCost = systemCosts.annualFuelCosts * Math.pow(1 + this.escalationRatesDecimal.naturalGas, year - 1);

      // CA DER charges
      const escalationFactor = Math.pow(1 + this.escalationRatesDecimal.energy, year - 1);
      const escalatedStandbyCharges = yearOneStandbyCharges * escalationFactor;
      const escalatedDepartingLoadCharges = yearOneDepartingLoadCharges * escalationFactor;
      const escalatedDerCharges = escalatedStandbyCharges + escalatedDepartingLoadCharges;

      // Total operating costs
      const totalOperatingCosts = escalatedOmCost + escalatedFuelCost + escalatedDerCharges;

      // Energy savings = BAU costs - Project costs
      const energySavings = bauUtilityCost - projectUtilityCost;

      // --- MACRS depreciation tax shield for this year ---
      let depreciationDeduction = 0;
      let depreciationTaxShield = 0;
      if (includeMacrs && year <= macrsDepreciationSchedule.length) {
        depreciationDeduction = depreciableBasis * (macrsDepreciationSchedule[year - 1] / 100);
        depreciationTaxShield = depreciationDeduction * taxRate;
      }

      // --- ITC cashflow (Year 1 only) ---
      const itcCashflow = (year === 1) ? itcCashflowYear1 : 0;

      // Net cash flow = savings - operating costs + tax benefits + ITC
      const netCashflow = energySavings - totalOperatingCosts + depreciationTaxShield + itcCashflow;

      cashflows.push({
        year,
        bauUtilityCost,
        projectUtilityCost,
        utilityCost: projectUtilityCost, // backward compatibility
        omCost: escalatedOmCost,
        fuelCost: escalatedFuelCost,
        derCharges: escalatedDerCharges,
        standbyCharges: escalatedStandbyCharges,
        departingLoadCharges: escalatedDepartingLoadCharges,
        totalOperatingCosts,
        energySavings,
        depreciationDeduction,
        depreciationTaxShield,
        itcCashflow,
        netCashflow,
        pvProduction: adjustedPvProduction,
      });
    }

    return cashflows;
  }

  calculateNpv(cashflows, discountRate = this.assumptions.discountRate) {
    return cashflows.reduce((npv, cf) => {
      const discountFactor = Math.pow(1 + discountRate, cf.year);
      return npv + cf.netCashflow / discountFactor;
    }, 0);
  }

  calculatePaybackPeriod(cashflows, initialInvestment) {
    let cumulativeCashflow = -initialInvestment;

    for (const cf of cashflows) {
      cumulativeCashflow += cf.netCashflow;
      if (cumulativeCashflow >= 0) {
        // Interpolate: how far into this year did cumulative cross zero?
        // cumulativeBeforeThisYear is negative, so -cumulativeBeforeThisYear is positive
        const cumulativeBeforeThisYear = cumulativeCashflow - cf.netCashflow;
        const fractionOfYear = Math.abs(cumulativeBeforeThisYear) / cf.netCashflow;
        return cf.year - 1 + fractionOfYear;
      }
    }

    return null; // No payback within analysis period
  }

  calculateDiscountedPaybackPeriod(cashflows, initialInvestment, discountRate = this.assumptions.discountRate) {
    let cumulativeDiscountedCashflow = -initialInvestment;

    for (const cf of cashflows) {
      const discountedCashflow = cf.netCashflow / Math.pow(1 + discountRate, cf.year);
      cumulativeDiscountedCashflow += discountedCashflow;
      if (cumulativeDiscountedCashflow >= 0) {
        const cumulativeBeforeThisYear = cumulativeDiscountedCashflow - discountedCashflow;
        const fractionOfYear = discountedCashflow > 0
          ? Math.abs(cumulativeBeforeThisYear) / discountedCashflow
          : 0;
        return cf.year - 1 + fractionOfYear;
      }
    }

    return null; // No payback within analysis period
  }

  calculateIrr(cashflows, initialInvestment, maxIterations = 100, tolerance = 0.0001) {
    if (!cashflows || cashflows.length === 0 || initialInvestment <= 0) return null;

    const allCashflows = [-initialInvestment, ...cashflows.map(cf => cf.netCashflow || 0)];
    const hasPositiveCashflow = allCashflows.slice(1).some(cf => cf > 0);
    if (!hasPositiveCashflow) return null;

    let irr = 0.1;
    for (let i = 0; i < maxIterations; i++) {
      let npv = 0;
      let dNpv = 0;
      for (let t = 0; t < allCashflows.length; t++) {
        const cf = allCashflows[t];
        npv += cf / Math.pow(1 + irr, t);
        if (t > 0) {
          dNpv -= t * cf / Math.pow(1 + irr, t + 1);
        }
      }
      if (Math.abs(npv) < tolerance) return irr;
      if (Math.abs(dNpv) < 1e-10) break;
      irr = irr - npv / dNpv;
      if (irr < -0.99 || irr > 10) break;
    }
    return irr > -0.99 && irr < 10 ? irr : null;
  }

  _calculateLCOE(systemCosts, totalOmCosts) {
    // LCOE = (Initial Investment + Present Value of O&M) / Present Value of Energy Production
    // Calculate total annual microgrid production from all sources
    let totalAnnualProduction = 0;
    let pvAnnualProduction = 0;
    let storageAnnualProduction = 0;
    let generatorAnnualProduction = 0;

    if (this.monthlyPerformanceData?.asset_performance) {
      // Sum up all monthly production from all generation sources
      this.monthlyPerformanceData.asset_performance.forEach(month => {
        pvAnnualProduction += month.pv_to_load_kwh || 0;
        storageAnnualProduction += month.storage_to_load_kwh || 0;
        generatorAnnualProduction += month.generator_to_load_kwh || 0;
      });
      totalAnnualProduction = pvAnnualProduction + storageAnnualProduction + generatorAnnualProduction;
    } else {
      // Fallback to PV only if monthly data not available
      totalAnnualProduction = this.systemData.pvAnnualProduction || 0;
      pvAnnualProduction = totalAnnualProduction;
    }

    if (!totalAnnualProduction || totalAnnualProduction === 0) {
      return 0; // No energy production
    }

    // Calculate present value of energy production over system lifetime
    let totalEnergyPV = 0;
    for (let year = 1; year <= this.assumptions.analysisYears; year++) {
      // Only PV degrades, storage and generator maintain constant output
      const pvDegradationFactor = Math.pow(1 - this.assumptions.degradationRate, year - 1);
      const adjustedPvProduction = pvAnnualProduction * pvDegradationFactor;
      const totalYearProduction = adjustedPvProduction + storageAnnualProduction + generatorAnnualProduction;

      // Discount to present value
      const discountFactor = Math.pow(1 + this.assumptions.discountRate, year);
      totalEnergyPV += totalYearProduction / discountFactor;
    }

    // Calculate present value of operating costs (O&M + Fuel + DER Charges)
    let operatingCostsPV = 0;
    const annualOmCosts = systemCosts.annualOmCosts;
    const annualFuelCosts = systemCosts.annualFuelCosts;
    // Handle yearOneDerCharges as object (new format) or number (legacy)
    const derChargesData = this.energyData.yearOneDerCharges;
    const annualDerCharges = typeof derChargesData === 'object' ? (derChargesData.total || 0) : (derChargesData || 0);

    for (let year = 1; year <= this.assumptions.analysisYears; year++) {
      // O&M costs escalate at labor rate
      const escalatedOmCost = annualOmCosts * Math.pow(1 + this.escalationRatesDecimal.labor, year - 1);
      // Fuel costs escalate at natural gas rate
      const escalatedFuelCost = annualFuelCosts * Math.pow(1 + this.escalationRatesDecimal.naturalGas, year - 1);
      // DER charges escalate at energy rate (regulatory charges tied to utility rates)
      const escalatedDerCharges = annualDerCharges * Math.pow(1 + this.escalationRatesDecimal.energy, year - 1);

      const discountFactor = Math.pow(1 + this.assumptions.discountRate, year);
      operatingCostsPV += (escalatedOmCost + escalatedFuelCost + escalatedDerCharges) / discountFactor;
    }

    // LCOE = (Initial Cost + PV of Operating Costs) / PV of Energy Production
    const lcoe = (systemCosts.netCapitalCost + operatingCostsPV) / totalEnergyPV;

    return lcoe;
  }

  _calculateUtilityLCOE() {
    // Calculate the equivalent LCOE if buying electricity from utility with escalation
    const { totalLoad, yearOneUtilityCostBau } = this.energyData;

    if (!totalLoad || totalLoad === 0 || !yearOneUtilityCostBau || yearOneUtilityCostBau === 0) {
      return 0;
    }

    const baseUtilityRate = yearOneUtilityCostBau / totalLoad;

    // Calculate present value of utility costs over system lifetime
    let totalUtilityCostPV = 0;
    let totalEnergyPV = 0;

    for (let year = 1; year <= this.assumptions.analysisYears; year++) {
      // Escalated utility rate (uses energy escalation)
      const escalatedUtilityRate = baseUtilityRate * Math.pow(1 + this.escalationRatesDecimal.energy, year - 1);

      // Annual utility cost at escalated rate
      const annualUtilityCost = totalLoad * escalatedUtilityRate;

      // Discount to present value
      const discountFactor = Math.pow(1 + this.assumptions.discountRate, year);
      totalUtilityCostPV += annualUtilityCost / discountFactor;
      totalEnergyPV += totalLoad / discountFactor;
    }

    // Utility LCOE = Present Value of Total Utility Costs / Present Value of Total Energy
    const utilityLcoe = totalEnergyPV > 0 ? totalUtilityCostPV / totalEnergyPV : 0;

    return utilityLcoe;
  }

  getComprehensiveAnalysis() {
    const systemCosts = this.calculateSystemCosts();
    const projectCashflows = this.calculateProjectCashflows();

    // Debug BAU data availability
    console.log('BAU Financial Data:', {
      yearOneUtilityCostBau: this.energyData.yearOneUtilityCostBau,
      lccBau: this.energyData.lccBau,
      yearOneOmCostsBau: this.energyData.yearOneOmCostsBau,
      totalLoad: this.energyData.totalLoad,
      baseUtilityRate: this.energyData.yearOneUtilityCostBau / this.energyData.totalLoad
    });

    // Calculate NPV of net cash flows - Initial investment already accounted for in netCashflow
    const npvOfCashflows = this.calculateNpv(projectCashflows);
    const projectNpv = npvOfCashflows - systemCosts.netCapitalCost;
    const paybackPeriod = this.calculatePaybackPeriod(projectCashflows, systemCosts.netCapitalCost);
    const discountedPaybackPeriod = this.calculateDiscountedPaybackPeriod(projectCashflows, systemCosts.netCapitalCost);
    const projectIrr = this.calculateIrr(projectCashflows, systemCosts.netCapitalCost);

    const totalSavings = projectCashflows.reduce((sum, cf) => sum + cf.energySavings, 0);
    const totalOmCosts = projectCashflows.reduce((sum, cf) => sum + cf.omCost, 0);
    const totalFuelCosts = projectCashflows.reduce((sum, cf) => sum + (cf.fuelCost || 0), 0);
    const totalDerCharges = projectCashflows.reduce((sum, cf) => sum + (cf.derCharges || 0), 0);
    const totalDepreciationTaxShield = projectCashflows.reduce((sum, cf) => sum + (cf.depreciationTaxShield || 0), 0);
    const totalItcCashflow = projectCashflows.reduce((sum, cf) => sum + (cf.itcCashflow || 0), 0);
    const totalOperatingCosts = totalOmCosts + totalFuelCosts + totalDerCharges;
    const netSavings = totalSavings - totalOperatingCosts - systemCosts.netCapitalCost + totalDepreciationTaxShield + totalItcCashflow;

    // Debug the calculations
    console.log('💰 FINANCIAL CALCULATION DEBUG:');
    console.log(`Analysis Years: ${this.assumptions.analysisYears}`);
    console.log(`Year 1 BAU Cost: $${this.energyData.yearOneUtilityCostBau?.toLocaleString()}`);
    console.log(`Year 1 Project Cost: $${this.energyData.yearOneUtilityCost?.toLocaleString()}`);
    console.log(`BAU Lifecycle Cost: $${this.energyData.lccBau?.toLocaleString()}`);
    console.log(`Total Savings (calculated): $${totalSavings.toLocaleString()}`);
    console.log(`Sample cashflow years 1-5:`, projectCashflows.slice(0, 5).map(cf => ({
      year: cf.year,
      bauCost: Math.round(cf.bauUtilityCost),
      projectCost: Math.round(cf.projectUtilityCost),
      savings: Math.round(cf.energySavings),
      omCost: Math.round(cf.omCost),
      fuelCost: Math.round(cf.fuelCost || 0)
    })));
    console.log(`Year 25 cashflow:`, {
      bauCost: Math.round(projectCashflows[24]?.bauUtilityCost || 0),
      projectCost: Math.round(projectCashflows[24]?.projectUtilityCost || 0),
      savings: Math.round(projectCashflows[24]?.energySavings || 0)
    });
    console.log(`O&M Costs: $${totalOmCosts.toLocaleString()}`);
    console.log(`Fuel Costs: $${totalFuelCosts.toLocaleString()}`);
    console.log(`CA DER Charges: $${totalDerCharges.toLocaleString()}`);
    console.log(`Total Operating Costs: $${totalOperatingCosts.toLocaleString()}`);
    console.log(`Net Capital Cost: $${systemCosts.netCapitalCost?.toLocaleString()}`);
    console.log(`NPV of Cashflows: $${npvOfCashflows.toLocaleString()}`);
    console.log(`Project NPV (after initial investment): $${projectNpv.toLocaleString()}`);
    console.log(`Payback Period: ${paybackPeriod} years`);
    console.log(`Discount Rate: ${(this.assumptions.discountRate * 100).toFixed(1)}%`);

    // Debug the summary object being returned
    const summaryBauRate = this.energyData.totalLoad > 0 ? this.energyData.yearOneUtilityCostBau / this.energyData.totalLoad : 0;
    console.log(`Summary BAU Rate calculation: $${this.energyData.yearOneUtilityCostBau?.toLocaleString()} ÷ ${this.energyData.totalLoad?.toLocaleString()} = $${summaryBauRate.toFixed(4)}/kWh`);

    // Create the return object and debug what's being passed to UI
    const returnData = {
      systemCosts,
      systemData: this.systemData,
      projectCashflows,
      summary: {
        initialInvestment: systemCosts.netCapitalCost,
        npv: projectNpv,
        irr: projectIrr,
        paybackPeriod,
        discountedPaybackPeriod,
        totalSavings,
        totalOmCosts,
        totalFuelCosts,
        totalDerCharges,
        totalDepreciationTaxShield,
        totalItcCashflow,
        // yearOneDerCharges is now an object with {total, standby, departingLoad}
        yearOneDerCharges: typeof this.energyData.yearOneDerCharges === 'object'
          ? this.energyData.yearOneDerCharges
          : { total: this.energyData.yearOneDerCharges || 0, standby: 0, departingLoad: 0 },
        totalOperatingCosts,
        netSavings,
        // Calculate LCOE (Levelized Cost of Energy) in $/kWh
        lcoe: this._calculateLCOE(systemCosts, totalOperatingCosts),
        // Calculate utility LCOE for comparison
        utilityLcoe: this._calculateUtilityLCOE(),
        savingsToInvestmentRatio: totalSavings > 0 ? netSavings / totalSavings : 0,
        // BAU baseline metrics
        bauYearOneUtilityCost: this.energyData.yearOneUtilityCostBau,
        bauLifecycleCost: this.energyData.lccBau,
        bauUtilityRate: this.energyData.totalLoad > 0 ? this.energyData.yearOneUtilityCostBau / this.energyData.totalLoad : 0,
        bauDataSource: this.energyData.bauDataSource,
        // Tax incentive settings active for this analysis
        taxIncentives: { ...this.taxIncentives },
      }
    };

    console.log(`Final summary.bauUtilityRate being returned: ${returnData.summary.bauUtilityRate}`);
    return returnData;
  }

  // ========================================
  // Static Deal Analysis Methods
  // ========================================

  /**
   * Calculate ESA revenue projection over contract term
   * Supports 4 pricing models: fixed_rate, savings_share, fixed_monthly, capacity_payment
   *
   * @param {Object} esaPricing - Pricing configuration
   * @param {string} esaPricing.model - 'fixed_rate' | 'savings_share' | 'fixed_monthly' | 'capacity_payment'
   * @param {number} esaPricing.rate - Rate per kWh (for fixed_rate)
   * @param {number} esaPricing.sharePercent - Percent of savings (for savings_share)
   * @param {number} esaPricing.payment - Monthly payment (for fixed_monthly)
   * @param {number} esaPricing.ratePerKw - Rate per kW per month (for capacity_payment)
   * @param {number} esaPricing.escalation - Annual escalation rate (e.g., 0.025 for 2.5%)
   * @param {number} esaPricing.term - Contract term in years
   * @param {number} annualProductionKwh - Annual system production in kWh
   * @param {number} annualSavings - Annual customer savings in $
   * @param {number} totalCapacityKw - Total system capacity in kW
   * @returns {Object} { annual: [{year, revenue, escalatedRate}], totalRevenue, avgAnnualRevenue }
   */
  static calculateEsaRevenue(esaPricing, annualProductionKwh, annualSavings, totalCapacityKw) {
    const { model, escalation = 0, term } = esaPricing;
    const annual = [];
    let totalRevenue = 0;

    for (let year = 1; year <= term; year++) {
      const escalationFactor = Math.pow(1 + escalation, year - 1);
      let revenue = 0;
      let escalatedRate = 0;

      switch (model) {
        case 'fixed_rate':
          escalatedRate = esaPricing.rate * escalationFactor;
          revenue = annualProductionKwh * escalatedRate;
          break;
        case 'savings_share':
          escalatedRate = esaPricing.sharePercent * escalationFactor;
          revenue = annualSavings * escalatedRate;
          break;
        case 'fixed_monthly':
          escalatedRate = esaPricing.payment * escalationFactor;
          revenue = escalatedRate * 12;
          break;
        case 'capacity_payment':
          escalatedRate = esaPricing.ratePerKw * escalationFactor;
          revenue = totalCapacityKw * escalatedRate * 12;
          break;
        default:
          throw new Error(`Unknown ESA pricing model: ${model}`);
      }

      annual.push({ year, revenue, escalatedRate });
      totalRevenue += revenue;
    }

    return {
      annual,
      totalRevenue,
      avgAnnualRevenue: totalRevenue / term
    };
  }

  /**
   * Calculate ESA operating expenses over contract term
   *
   * @param {Object} systemSizes - System component sizes
   * @param {number} systemSizes.pvKw - PV capacity in kW
   * @param {number} systemSizes.batteryKwh - Battery capacity in kWh
   * @param {number} systemSizes.generatorKw - Generator capacity in kW
   * @param {Object} operatingCosts - Operating cost rates
   * @param {number} operatingCosts.omPerKwYear - O&M cost per kW per year for PV
   * @param {number} operatingCosts.omPerKwhYear - O&M cost per kWh per year for battery
   * @param {number} operatingCosts.omPerKwGenYear - O&M cost per kW per year for generator
   * @param {number} operatingCosts.insurancePercent - Insurance as percent of system cost
   * @param {number} operatingCosts.assetMgmtPerYear - Asset management cost per year
   * @param {number} systemCost - Total system cost
   * @param {number} term - Contract term in years
   * @param {number} omEscalation - O&M escalation rate (default 0.02)
   * @returns {Object} { annual: [{year, om, insurance, assetMgmt, total}], totalExpenses }
   */
  static calculateEsaExpenses(systemSizes, operatingCosts, systemCost, term, omEscalation = 0.02) {
    const { pvKw = 0, batteryKwh = 0, generatorKw = 0 } = systemSizes;
    const {
      omPerKwYear = 0,
      omPerKwhYear = 0,
      omPerKwGenYear = 0,
      insurancePercent = 0,
      assetMgmtPerYear = 0
    } = operatingCosts;

    const annual = [];
    let totalExpenses = 0;

    // Base O&M cost (year 1)
    const baseOm = (pvKw * omPerKwYear) + (batteryKwh * omPerKwhYear) + (generatorKw * omPerKwGenYear);

    // Insurance is typically flat (based on initial system cost)
    const insurance = systemCost * insurancePercent;

    for (let year = 1; year <= term; year++) {
      const escalationFactor = Math.pow(1 + omEscalation, year - 1);
      const om = baseOm * escalationFactor;
      const total = om + insurance + assetMgmtPerYear;

      annual.push({
        year,
        om,
        insurance,
        assetMgmt: assetMgmtPerYear,
        total
      });

      totalExpenses += total;
    }

    return {
      annual,
      totalExpenses
    };
  }

  /**
   * Calculate developer returns WITH and WITHOUT ITC for risk assessment
   *
   * @param {number} initialCost - Initial system cost
   * @param {number[]} annualCashFlows - Array of annual cash flows
   * @param {number} itcAmount - ITC credit amount
   * @param {number} discountRate - Discount rate (default 0.08)
   * @returns {Object} { withoutItc: {...}, withItc: {...} }
   */
  static calculateDeveloperReturns(initialCost, annualCashFlows, itcAmount, discountRate = 0.08) {
    const calculateMetrics = (investment, cashFlows) => {
      // Calculate NPV
      let npv = -investment;
      for (let i = 0; i < cashFlows.length; i++) {
        npv += cashFlows[i] / Math.pow(1 + discountRate, i + 1);
      }

      // Calculate payback period
      let cumulative = -investment;
      let paybackYears = null;
      for (let i = 0; i < cashFlows.length; i++) {
        cumulative += cashFlows[i];
        if (cumulative >= 0 && paybackYears === null) {
          const prevCumulative = cumulative - cashFlows[i];
          paybackYears = i + (Math.abs(prevCumulative) / cashFlows[i]);
        }
      }

      // Calculate IRR using Newton-Raphson
      let irr = 0.1;
      const allCashFlows = [-investment, ...cashFlows];
      for (let iter = 0; iter < 100; iter++) {
        let fVal = 0;
        let fPrime = 0;
        for (let t = 0; t < allCashFlows.length; t++) {
          const factor = Math.pow(1 + irr, t);
          fVal += allCashFlows[t] / factor;
          if (t > 0) {
            fPrime -= t * allCashFlows[t] / (factor * (1 + irr));
          }
        }
        if (Math.abs(fVal) < 0.0001) break;
        if (Math.abs(fPrime) < 1e-10) break;
        irr = irr - fVal / fPrime;
        if (irr < -0.99) irr = -0.99;
        if (irr > 10) irr = 10;
      }

      // Total profit
      const totalCashInflows = cashFlows.reduce((sum, cf) => sum + cf, 0);
      const totalProfit = totalCashInflows - investment;

      return {
        initialInvestment: investment,
        netCashFlows: cashFlows,
        irr: (irr > -0.99 && irr < 10) ? irr : null,
        npv,
        paybackYears,
        totalProfit
      };
    };

    return {
      withoutItc: calculateMetrics(initialCost, annualCashFlows),
      withItc: calculateMetrics(initialCost - itcAmount, annualCashFlows)
    };
  }

  /**
   * Calculate developer margin for CapEx sale with/without ITC
   *
   * @param {Object} internalCosts - Internal cost breakdown
   * @param {number} internalCosts.totalInternalCost - Total internal cost
   * @param {number} internalCosts.itcEligibleCost - Portion eligible for ITC
   * @param {number} internalCosts.itcRate - ITC rate (e.g., 0.30)
   * @param {number} salePrice - Sale price to customer
   * @param {boolean} customerItcEligible - Whether customer can claim ITC
   * @returns {Object} Margin metrics
   */
  static calculateCapexMargin(internalCosts, salePrice, customerItcEligible) {
    const { totalInternalCost, itcEligibleCost, itcRate } = internalCosts;

    // Gross margin (simple profit)
    const grossMargin = salePrice - totalInternalCost;
    const grossMarginPercent = grossMargin / salePrice;

    // Developer ITC (based on their internal eligible costs)
    const developerItc = itcEligibleCost * itcRate;

    // Net margin (including developer's ITC benefit)
    const netMargin = grossMargin + developerItc;
    const netMarginPercent = netMargin / totalInternalCost;

    // Effective margin considering ITC
    const effectiveMargin = netMargin;
    const effectiveMarginPercent = netMarginPercent;

    // Customer ITC (if eligible, based on sale price pro-rated by eligible portion)
    let customerItc = 0;
    if (customerItcEligible) {
      const eligibleRatio = itcEligibleCost / totalInternalCost;
      const customerItcBase = salePrice * eligibleRatio;
      customerItc = customerItcBase * itcRate;
    }

    return {
      grossMargin,
      grossMarginPercent,
      netMargin,
      netMarginPercent,
      developerItc,
      effectiveMargin,
      effectiveMarginPercent,
      customerItc
    };
  }

  /**
   * Calculate customer value metrics for ESA deals
   *
   * @param {number[]} annualUtilitySavings - Array of annual utility savings
   * @param {number[]} esaPayments - Array of annual ESA payments
   * @param {number} currentUtilityRate - Current utility rate $/kWh
   * @param {number} esaRate - ESA rate $/kWh
   * @param {number} discountRate - Discount rate (default 0.08)
   * @returns {Object} Customer value metrics
   */
  static calculateEsaCustomerValue(annualUtilitySavings, esaPayments, currentUtilityRate, esaRate, discountRate = 0.08) {
    const years = annualUtilitySavings.length;

    // Year 1 savings
    const year1Savings = annualUtilitySavings[0] - esaPayments[0];

    // Lifetime savings (nominal)
    let lifetimeSavings = 0;
    for (let i = 0; i < years; i++) {
      lifetimeSavings += annualUtilitySavings[i] - esaPayments[i];
    }

    // NPV of savings
    let npvOfSavings = 0;
    for (let i = 0; i < years; i++) {
      const annualNetSavings = annualUtilitySavings[i] - esaPayments[i];
      npvOfSavings += annualNetSavings / Math.pow(1 + discountRate, i + 1);
    }

    // Rate discount (comparing utility rate to ESA rate)
    const rateDiscount = (currentUtilityRate - esaRate) / currentUtilityRate;

    // Average monthly calculations (based on year 1)
    const avgMonthlyBill = {
      savings: annualUtilitySavings[0] / 12,
      esaPayment: esaPayments[0] / 12,
      netSavings: year1Savings / 12
    };

    return {
      year1Savings,
      lifetimeSavings,
      npvOfSavings,
      rateDiscount,
      avgMonthlyBill
    };
  }
}

function formatValue(val, type) {
  if (val === undefined || val === null) return '—';
  if (type === 'currency') return formatCurrency(val);
  if (type === 'lcoe') return `$${val.toFixed(3)}/kWh`; // Show LCOE with 3 decimal places
  if (type === 'lcoe_comparison') {
    if (!val.solar || !val.utility) return '—';
    const savings = ((val.utility - val.solar) / val.utility * 100).toFixed(1);
    return (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          Microgrid: ${val.solar.toFixed(3)}/kWh
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Utility: ${val.utility.toFixed(3)}/kWh
        </Typography>
        <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 'bold' }}>
          ({savings}% savings)
        </Typography>
      </Box>
    );
  }
  if (type === 'percent') return `${(val * 100).toFixed(2)}%`;
  if (type === 'years') return `${val.toFixed(1)} years`;
  if (type === 'number') return formatNumber(val, 0);
  return val;
}

// Calculate Internal Rate of Return (IRR) using Newton-Raphson method
function calculateIRR(cashflows, initialInvestment, maxIterations = 100, tolerance = 0.0001) {
  if (!cashflows || cashflows.length === 0 || initialInvestment <= 0) return null;

  // We need the full cashflow array including year 0 (negative initial investment)
  const allCashflows = [-initialInvestment, ...cashflows.map(cf => cf.netCashflow || 0)];

  // Check if all cashflows after Year 0 are negative (no positive returns)
  const hasPositiveCashflow = allCashflows.slice(1).some(cf => cf > 0);
  if (!hasPositiveCashflow) return null;

  // Newton-Raphson method
  let irr = 0.1; // Initial guess 10%

  for (let i = 0; i < maxIterations; i++) {
    let npv = 0;
    let dNpv = 0; // Derivative of NPV

    for (let t = 0; t < allCashflows.length; t++) {
      const cf = allCashflows[t];
      npv += cf / Math.pow(1 + irr, t);
      if (t > 0) {
        dNpv -= t * cf / Math.pow(1 + irr, t + 1);
      }
    }

    if (Math.abs(npv) < tolerance) {
      return irr;
    }

    if (Math.abs(dNpv) < 1e-10) {
      break; // Avoid division by zero
    }

    irr = irr - npv / dNpv;

    // Bounds check
    if (irr < -0.99 || irr > 10) {
      break;
    }
  }

  return irr > -0.99 && irr < 10 ? irr : null;
}

// Export cash flow table to CSV
function exportCashFlowToCSV(cashflowData, initialInvestment, itcAmount, filename = 'cash_flow_analysis.csv') {
  const hasDepreciation = cashflowData.some(cf => (cf.depreciationTaxShield || 0) > 0);
  const hasItcCashflow = cashflowData.some(cf => (cf.itcCashflow || 0) > 0);

  const headers = [
    'Year', 'Initial Investment', 'ITC Credit',
    'BAU Utility Cost', 'Project Utility Cost', 'Energy Savings',
    'O&M Costs', 'Fuel Costs', 'Standby Charges',
    ...(hasDepreciation ? ['Depreciation Tax Shield'] : []),
    ...(hasItcCashflow ? ['ITC Cashflow'] : []),
    'Net Cash Flow', 'Cumulative Cash Flow', 'PV Production (kWh)'
  ];

  // Year 0: ITC as upfront only if NOT modeled as cashflow
  const year0Itc = hasItcCashflow ? 0 : itcAmount;
  const rows = [
    ['0', (-initialInvestment).toFixed(2), year0Itc.toFixed(2), '0', '0', '0', '0', '0', '0',
     ...(hasDepreciation ? ['0'] : []),
     ...(hasItcCashflow ? ['0'] : []),
     (-initialInvestment + year0Itc).toFixed(2), (-initialInvestment + year0Itc).toFixed(2), '0']
  ];

  // Years 1-N rows
  let cumulative = -initialInvestment + year0Itc;
  cashflowData.forEach(cf => {
    cumulative += cf.netCashflow || 0;
    rows.push([
      cf.year.toString(),
      '0',
      '0',
      (cf.bauUtilityCost || 0).toFixed(2),
      (cf.projectUtilityCost || 0).toFixed(2),
      (cf.energySavings || 0).toFixed(2),
      (cf.omCost || 0).toFixed(2),
      (cf.fuelCost || 0).toFixed(2),
      (cf.standbyCharges || 0).toFixed(2),
      ...(hasDepreciation ? [(cf.depreciationTaxShield || 0).toFixed(2)] : []),
      ...(hasItcCashflow ? [(cf.itcCashflow || 0).toFixed(2)] : []),
      (cf.netCashflow || 0).toFixed(2),
      cumulative.toFixed(2),
      (cf.pvProduction || 0).toFixed(0)
    ]);
  });

  const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
}

function formatInputNumber(value) {
  // Handle empty or invalid input
  if (!value || value === '') return '';

  // Remove existing commas and ensure it's a number
  const num = parseFloat(value.toString().replace(/,/g, ''));
  if (isNaN(num)) return '';

  // Add commas for thousands
  return num.toLocaleString('en-US');
}

export default function FinancialAnalysis({ results, capexSource: propCapexSource, setCapexSource: propSetCapexSource, selectedBatteryData, includeItc: propIncludeItc, setIncludeItc: propSetIncludeItc, budget, systemSizes, analysis: propAnalysis, monthlyPerformanceData, commercialAssumptions }) {
  const [tab, setTab] = useState(0);

  // Use commercial assumptions from prop (from Commercial Assumptions tab)
  // Fall back to defaults if not provided
  const customAssumptions = useMemo(() => ({
    analysisYears: commercialAssumptions?.calculatorInputs?.analysisYears || 20,
    discountRate: commercialAssumptions?.calculatorInputs?.discountRate || 0.08,
  }), [commercialAssumptions]);

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Fetch admin-managed CapEx default rates
  const capexRates = useCapexRates();


  // CAPEX state
  const [capexInputs, setCapexInputs] = useState({
    pv: { costType: 'per_kw', rate: 1500, totalCost: 0 },
    battery: { costType: 'per_kwh', rate: 500, totalCost: 0 },
    generator: { costType: 'per_kw', rate: 600, totalCost: 0 },
    installation: {
      pv: { costType: 'per_kw', rate: 500, totalCost: 0 },
      battery: { costType: 'per_kwh', rate: 300, totalCost: 0 },
      generator: { costType: 'per_kw', rate: 200, totalCost: 0 }
    },
    softCosts: {
      engineering: { costType: 'percentage', rate: 5, totalCost: 0 },
      permits: { costType: 'percentage', rate: 2, totalCost: 0 },
      projectManagement: { costType: 'percentage', rate: 3, totalCost: 0 },
      commissioning: { costType: 'percentage', rate: 1.5, totalCost: 0 },
      contingency: { costType: 'percentage', rate: 10, totalCost: 0 }
    }
  });

  // Financing mode: 'capex' (upfront purchase) or 'ppa' (PPA/ESA financed)
  const [financingMode, setFinancingMode] = useState('capex');

  // CapEx source: use props if provided (for persistence), otherwise fallback to local state
  const [localCapexSource, setLocalCapexSource] = useState('nrel');
  const capexSource = propCapexSource !== undefined ? propCapexSource : localCapexSource;
  const setCapexSource = propSetCapexSource || setLocalCapexSource;

  // PPA/ESA pricing inputs
  const [ppaInputs, setPpaInputs] = useState({
    solarPpaRate: 0.10,  // $/kWh for solar PPA
    batteryEsaRate: 15,  // $/kW-month for battery ESA
    escalator: 2.5,  // % annual escalation
    termYears: 20  // PPA/ESA contract term
  });

  // ITC (Investment Tax Credit) toggle - use props if provided, fallback to local state
  const [localIncludeItc, setLocalIncludeItc] = useState(true);
  const includeItc = propIncludeItc !== undefined ? propIncludeItc : localIncludeItc;
  const setIncludeItc = propSetIncludeItc || setLocalIncludeItc;

  // Sync capexInputs from Budget tab when available (Budget tab is the source of truth)
  useEffect(() => {
    if (budget?.capexInputs) {
      setCapexInputs(budget.capexInputs);
      console.log('📊 [Financial Analysis] Synced capexInputs from Budget tab');
    }
  }, [budget?.capexInputs]);

  // Use the analysis prop from ScenarioEditor as the single source of truth
  // This ensures consistency between Financials tab and Executive Summary
  const baseAnalysis = propAnalysis;
  // Calculate PPA/ESA-based financials
  const ppaFinancials = useMemo(() => {
    if (financingMode !== 'ppa' || !baseAnalysis?.systemData) return null;

    const { systemData, projectCashflows } = baseAnalysis;
    const pvSize = systemData.pvSize || 0;
    const storageKw = systemData.storageSize || 0;

    // Calculate annual PPA/ESA payments
    const yearlyPpaPayments = projectCashflows.map((cf, idx) => {
      const year = idx + 1;
      const escalationFactor = Math.pow(1 + ppaInputs.escalator / 100, year - 1);

      // Solar PPA: charged per kWh produced
      const solarProduction = cf.solarProduction || 0;
      const solarPpaPayment = year <= ppaInputs.termYears
        ? solarProduction * ppaInputs.solarPpaRate * escalationFactor
        : 0;

      // Battery ESA: charged per kW-month (capacity payment)
      const batteryEsaPayment = year <= ppaInputs.termYears
        ? storageKw * ppaInputs.batteryEsaRate * 12 * escalationFactor
        : 0;

      return {
        year,
        solarPpaPayment,
        batteryEsaPayment,
        totalPpaPayment: solarPpaPayment + batteryEsaPayment
      };
    });

    // Recalculate cash flows with PPA/ESA costs
    const ppaCashflows = projectCashflows.map((cf, idx) => {
      const ppaPayment = yearlyPpaPayments[idx];
      const utilitySavings = cf.energySavings; // Utility cost savings (BAU - with system)
      const netCashflow = utilitySavings - ppaPayment.totalPpaPayment - cf.omCost;

      return {
        ...cf,
        ppaPayment: ppaPayment.totalPpaPayment,
        utilitySavings,
        netCashflow
      };
    });

    // Calculate NPV with PPA/ESA
    const discountRate = customAssumptions.discountRate;
    const npv = ppaCashflows.reduce((sum, cf, idx) => {
      const year = idx + 1;
      const discountFactor = Math.pow(1 + discountRate, year);
      return sum + (cf.netCashflow / discountFactor);
    }, 0);

    // Calculate payback period
    let cumulativeCashflow = 0;
    let paybackYear = 0;
    for (let i = 0; i < ppaCashflows.length; i++) {
      cumulativeCashflow += ppaCashflows[i].netCashflow;
      if (cumulativeCashflow >= 0) {
        paybackYear = i + 1;
        break;
      }
    }

    // Total savings over analysis period
    const totalSavings = ppaCashflows
      .slice(0, customAssumptions.analysisYears)
      .reduce((sum, cf) => sum + cf.netCashflow, 0);

    return {
      yearlyPpaPayments,
      ppaCashflows,
      npv,
      paybackYear,
      totalSavings,
      initialInvestment: 0, // No upfront cost in PPA/ESA
      netSavings: totalSavings // Same as total savings since no upfront cost
    };
  }, [financingMode, ppaInputs, baseAnalysis, customAssumptions]);

  // Initialize CAPEX inputs with admin-managed defaults (fetched from API)
  // When a specific battery is selected from DB, use its cost info instead of generic defaults
  useEffect(() => {
    if (baseAnalysis?.systemData && !capexRates.loading) {
      const { systemData } = baseAnalysis;
      const pvSize = systemData.pvPrescribed || systemData.pvSize || 0;
      const storageKwh = systemData.storageCapacityPrescribed || systemData.storageCapacity || 0;
      const storageKw = systemData.storagePowerPrescribed || systemData.storagePower || 0;
      const genSize = systemData.generatorPrescribed || systemData.generatorSize || 0;

      // Use admin-managed rates from the pricing database (fetched via useCapexRates hook)
      const pvTurnkeyRate = capexRates.pvTurnkeyRate;

      // Battery costs: Use selected battery's costs if available, otherwise use admin defaults
      const batteryEquipmentRate = selectedBatteryData?.cost_per_kwh ?? capexRates.batteryEquipmentRate;
      const batteryPowerRate = selectedBatteryData?.cost_per_kw ?? capexRates.batteryPowerRate;
      const batteryInstallRate = selectedBatteryData?.install_cost_per_kwh ?? capexRates.batteryInstallRate;

      const generatorEquipmentRate = capexRates.generatorEquipmentRate;
      const generatorInstallRate = capexRates.generatorInstallRate;
      const softCosts = capexRates.softCostRates;

      // Calculate equipment totals
      const pvTurnkeyTotal = pvSize * pvTurnkeyRate;
      // Battery cost = ($/kWh × kWh) + ($/kW × kW) for proper accounting of energy + power components
      const batteryEquipmentTotal = (storageKwh * batteryEquipmentRate) + (storageKw * batteryPowerRate);
      const batteryInstallTotal = storageKwh * batteryInstallRate;
      const generatorEquipmentTotal = genSize * generatorEquipmentRate;
      const generatorInstallTotal = genSize * generatorInstallRate;

      // Soft costs as percentage of total equipment + installation
      const equipmentInstallBase = pvTurnkeyTotal + batteryEquipmentTotal + batteryInstallTotal + generatorEquipmentTotal + generatorInstallTotal;

      setCapexInputs(prev => ({
        ...prev,
        pvTurnkey: {
          costType: 'per_kw',
          rate: pvTurnkeyRate,
          totalCost: pvTurnkeyTotal
        },
        battery: {
          costType: 'per_kwh',
          rate: batteryEquipmentRate,
          totalCost: batteryEquipmentTotal
        },
        generator: {
          costType: 'per_kw',
          rate: generatorEquipmentRate,
          totalCost: generatorEquipmentTotal
        },
        installation: {
          battery: {
            costType: 'per_kwh',
            rate: batteryInstallRate,
            totalCost: batteryInstallTotal
          },
          generator: {
            costType: 'per_kw',
            rate: generatorInstallRate,
            totalCost: generatorInstallTotal
          }
        },
        softCosts: {
          engineering: { costType: 'percentage', rate: softCosts.engineering, totalCost: equipmentInstallBase * (softCosts.engineering / 100) },
          permits: { costType: 'percentage', rate: softCosts.permits, totalCost: equipmentInstallBase * (softCosts.permits / 100) },
          projectManagement: { costType: 'percentage', rate: softCosts.projectManagement, totalCost: equipmentInstallBase * (softCosts.projectManagement / 100) },
          commissioning: { costType: 'percentage', rate: softCosts.insurance || 1, totalCost: equipmentInstallBase * ((softCosts.insurance || 1) / 100) },
          contingency: { costType: 'percentage', rate: softCosts.contingency, totalCost: equipmentInstallBase * (softCosts.contingency / 100) }
        }
      }));
    }
  }, [baseAnalysis?.systemData, capexRates.loading, capexRates.pvTurnkeyRate, capexRates.batteryEquipmentRate, capexRates.batteryPowerRate, capexRates.batteryInstallRate, capexRates.generatorEquipmentRate, capexRates.generatorInstallRate, capexRates.softCostRates, selectedBatteryData]);

  // CRITICAL: Use Budget tab's CapEx calculations directly (single source of truth)
  // DO NOT recalculate - Budget tab is authoritative
  const capexCalculations = useMemo(() => {
    if (budget?.capexCalculations) {
      console.log('📊 [FinancialAnalysis] Using Budget CapEx:', budget.capexCalculations.total);
      return budget.capexCalculations;
    }

    // Fallback only if Budget not available (shouldn't happen in normal flow)
    console.warn('⚠️ [FinancialAnalysis] Budget CapEx not available, using empty calculations');
    return { equipment: 0, installation: 0, softCosts: 0, total: 0, components: {} };
  }, [budget?.capexCalculations]);

  // OLD CODE - REMOVED: Was recalculating CapEx locally, causing discrepancies
  /* const capexCalculations = useMemo(() => {
    if (!baseAnalysis?.systemData) {
      console.log('No systemData available for CAPEX calculations');
      return { equipment: 0, installation: 0, softCosts: 0, total: 0, components: {} };
    }

    // ... old recalculation code removed ...
  }, [baseAnalysis?.systemData, capexInputs]); */

  // ITC (Investment Tax Credit) percentages per individual line item
  const [itcInputs, setItcInputs] = useState({
    // Turnkey
    pvTurnkey: 30,
    // Equipment
    batteryEquipment: 30,
    generatorEquipment: 0,
    // Installation
    batteryInstallation: 30,
    generatorInstallation: 0,
    // Soft Costs
    engineering: 30,
    permits: 30,
    projectManagement: 30,
    commissioning: 30,
    contingency: 30,
  });

  // Helper to update ITC for a specific line item
  const updateItcInput = (key, value) => {
    setItcInputs(prev => ({ ...prev, [key]: parseFloat(value) || 0 }));
  };

  // Sync itcInputs from Budget tab when available
  useEffect(() => {
    if (budget?.itcInputs) {
      setItcInputs(budget.itcInputs);
      console.log('📊 [Financial Analysis] Synced itcInputs from Budget tab');
    }
  }, [budget?.itcInputs]);

  // CRITICAL: Use Budget tab's ITC calculations directly (single source of truth)
  // DO NOT recalculate - Budget tab is authoritative
  const itcCalculations = useMemo(() => {
    if (budget?.itcCalculations) {
      console.log('📊 [FinancialAnalysis] Using Budget ITC:', budget.itcCalculations.totalItc);
      return budget.itcCalculations;
    }

    // Fallback only if Budget not available (shouldn't happen in normal flow)
    console.warn('⚠️ [FinancialAnalysis] Budget ITC not available, using empty calculations');
    return {
      totalItc: 0,
      totalUpfront: 0,
      netCostAfterItc: 0,
      effectiveItcRate: 0,
      itcEnabled: includeItc
    };
  }, [budget?.itcCalculations, includeItc]);

  // OLD CODE - REMOVED: Was recalculating ITC locally, causing discrepancies
  /* const itcCalculations = useMemo(() => {
    // ... old recalculation code removed ...
  }, [capexCalculations, itcInputs, includeItc]); */

  // Use analysis from parent (ScenarioEditor) - single source of truth
  // This ensures Performance and Financials tabs always show the same values
  const analysis = propAnalysis;

  // Update handlers
  const updateCapexInput = (category, field, value) => {
    setCapexInputs(prev => {
      const newInputs = { ...prev };

      if (field === 'costType') {
        if (value === 'lump_sum') {
          // When switching to lump sum, set totalCost to current calculated cost
          let currentCost = 0;
          if (category === 'pvTurnkey') {
            currentCost = capexCalculations.components?.pvTurnkey?.total || 0;
          } else {
            currentCost = capexCalculations.components?.[category]?.equipment || 0;
          }
          newInputs[category] = {
            ...prev[category],
            [field]: value,
            totalCost: currentCost
          };
        } else {
          // When switching to rate mode (per_kw, per_kwh), just update the costType
          // Keep existing rate value
          newInputs[category] = {
            ...prev[category],
            [field]: value
          };
        }
      } else {
        // Normal field update
        newInputs[category] = {
          ...prev[category],
          [field]: typeof value === 'string' ? parseFloat(value) || 0 : value
        };
      }

      return newInputs;
    });
  };

  const updateInstallationCost = (category, field, value) => {
    setCapexInputs(prev => {
      const newInputs = { ...prev };

      if (field === 'costType') {
        if (value === 'lump_sum') {
          // When switching to lump sum, set totalCost to current calculated installation cost
          const currentInstallCost = capexCalculations.components?.[category]?.installation || 0;
          newInputs.installation[category] = {
            ...prev.installation[category],
            [field]: value,
            totalCost: currentInstallCost
          };
        } else {
          // When switching to rate mode, keep existing rate value
          newInputs.installation[category] = {
            ...prev.installation[category],
            [field]: value
          };
        }
      } else {
        // Normal field update
        newInputs.installation[category] = {
          ...prev.installation[category],
          [field]: typeof value === 'string' ? parseFloat(value) || 0 : value
        };
      }

      return newInputs;
    });
  };

  const updateSoftCost = (category, field, value) => {
    setCapexInputs(prev => {
      const newInputs = { ...prev };

      if (field === 'costType') {
        if (value === 'lump_sum') {
          // When switching to lump sum, set totalCost to current calculated percentage cost
          const currentSoftCost = capexCalculations.softCosts?.[category] || 0;
          newInputs.softCosts[category] = {
            ...prev.softCosts[category],
            [field]: value,
            totalCost: currentSoftCost
          };
        } else {
          // When switching to percentage mode, keep existing rate
          newInputs.softCosts[category] = {
            ...prev.softCosts[category],
            [field]: value
          };
        }
      } else {
        // Normal field update
        newInputs.softCosts[category] = {
          ...prev.softCosts[category],
          [field]: typeof value === 'string' ? parseFloat(value) || 0 : value
        };
      }

      return newInputs;
    });
  };


  if (!results || !analysis) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>No financial data available for analysis.</Typography>
          <Typography variant="body2" color="text.secondary">
            Please run an optimization analysis to generate financial results.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Check if BAU data is available and from a valid source
  const hasValidBauData = analysis?.summary?.bauYearOneUtilityCost > 0 || analysis?.summary?.bauLifecycleCost > 0;
  const isValidDataSource = !analysis?.summary?.bauDataSource ||
    analysis.summary.bauDataSource === 'monthly_performance' ||
    analysis.summary.bauDataSource === 'reopt' ||
    analysis.summary.bauDataSource === 'backend_calculator';

  // Only show error if we have no BAU data AND an invalid data source
  // Allow display even if optimal system size is 0 kW (no viable solution)
  if (!hasValidBauData && !isValidDataSource) {
    return (
      <Card>
        <CardContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Limited Financial Data Available
            </Typography>
            <Typography variant="body2">
              The optimization model returned incomplete baseline (Business-as-Usual) financial data.
              Some financial metrics may not be available.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Possible causes:</strong>
            </Typography>
            <ul>
              <li>Missing utility tariff information</li>
              <li>Invalid load profile data</li>
              <li>Off-grid scenario without proper baseline configuration</li>
            </ul>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Please verify your scenario inputs and try running the optimization again.
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const { systemCosts, systemData, projectCashflows, summary } = analysis;

  // Prepare chart data - use PPA cashflows if in PPA mode
  const activeCashflows = financingMode === 'ppa' && ppaFinancials
    ? ppaFinancials.ppaCashflows
    : projectCashflows;

  const filteredCashflows = activeCashflows.filter(cf => cf.year <= customAssumptions.analysisYears);

  const cashflowChartData = filteredCashflows.map(cf => {
    const initialInvestment = financingMode === 'ppa' ? 0 : systemCosts.netCapitalCost;

    return {
      year: cf.year,
      energySavings: cf.energySavings || cf.utilitySavings,
      bauUtilityCost: cf.bauUtilityCost || 0,
      projectUtilityCost: cf.projectUtilityCost || cf.utilityCost || 0,
      omCost: -cf.omCost,
      fuelCost: -(cf.fuelCost || 0),
      standbyCharges: -(cf.standbyCharges || 0), // Separate standby charges (departing load rolled into utility)
      derCharges: -(cf.derCharges || 0), // Keep for backwards compatibility
      ppaCost: financingMode === 'ppa' ? -(cf.ppaPayment || 0) : 0,
      netCashflow: cf.netCashflow,
      cumulativeNetCashflow: filteredCashflows
        .slice(0, cf.year)
        .reduce((sum, c) => sum + c.netCashflow, -initialInvestment),
    };
  });

  // Calculate costs based on installed capacity, not utilized
  const pvCostAdjusted = systemData?.pvPrescribed && systemData?.pvSize !== systemData?.pvPrescribed
    ? (systemData?.pvCost || 0) * (systemData?.pvPrescribed / (systemData?.pvSize || 1))
    : (systemData?.pvCost || 0);

  const storageCostAdjusted = systemData?.storagePrescribed && systemData?.storageSize !== systemData?.storagePrescribed
    ? (systemData?.storageCost || 0) * (systemData?.storagePrescribed / (systemData?.storageSize || 1))
    : (systemData?.storageCost || 0);

  const generatorCostAdjusted = systemData?.generatorPrescribed && systemData?.generatorSize !== systemData?.generatorPrescribed
    ? (systemData?.generatorCost || 0) * (systemData?.generatorPrescribed / (systemData?.generatorSize || 1))
    : (systemData?.generatorCost || 0);

  const costBreakdownData = [
    { name: 'Solar PV', value: pvCostAdjusted, color: '#ffc658' },
    { name: 'Battery Storage', value: storageCostAdjusted, color: '#82ca9d' },
    { name: 'Generator', value: generatorCostAdjusted, color: '#ff7c7c' },
    { name: 'Other', value: Math.max(0, systemCosts.netCapitalCost - pvCostAdjusted - storageCostAdjusted - generatorCostAdjusted), color: '#8dd1e1' },
  ].filter(item => item.value > 0);

  const summaryMetrics = [
    { label: 'Initial Investment', value: summary.initialInvestment, type: 'currency' },
    { label: `NPV (${customAssumptions.analysisYears} years)`, value: summary.npv, type: 'currency' },
    { label: 'Payback Period', value: summary.paybackPeriod, type: 'years' },
    { label: 'Total Savings', value: summary.totalSavings, type: 'currency' },
    { label: 'Net Savings', value: summary.netSavings, type: 'currency' },
    { label: 'LCOE', value: { solar: summary.lcoe, utility: summary.utilityLcoe }, type: 'lcoe_comparison' },
  ];

  // Use PPA financials if in PPA mode, otherwise use CapEx
  const activeFinancials = financingMode === 'ppa' && ppaFinancials ? ppaFinancials : {
    npv: summary.npv,
    paybackYear: summary.paybackPeriod,
    totalSavings: summary.totalSavings,
    initialInvestment: summary.initialInvestment,
    netSavings: summary.netSavings,
    ppaCashflows: projectCashflows
  };

  // Update summary metrics based on financing mode
  const updatedSummaryMetrics = [
    ...(financingMode === 'capex' ? [{ label: 'Initial Investment', value: activeFinancials.initialInvestment, type: 'currency' }] : []),
    { label: `NPV (${customAssumptions.analysisYears} years)`, value: activeFinancials.npv, type: 'currency' },
    { label: 'Payback Period', value: activeFinancials.paybackYear, type: 'years' },
    { label: 'Total Savings', value: activeFinancials.totalSavings, type: 'currency' },
    ...(financingMode === 'capex' ? [{ label: 'Net Savings', value: activeFinancials.netSavings, type: 'currency' }] : []),
    { label: 'LCOE', value: { solar: summary.lcoe, utility: summary.utilityLcoe }, type: 'lcoe_comparison' },
  ];

  if (monthlyPerformanceData?.tariff_data_source === 'missing') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            ⛔ No Tariff Selected
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Financial analysis cannot be performed without a utility tariff.
            <br />
            Please go to the <strong>Utility</strong> tab in the Scenario Editor and select a tariff.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Financial Analysis
      </Typography>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Summary" />
        <Tab label="Cash Flow Analysis" />
        <Tab label="CAPEX" />
      </Tabs>


      {tab === 0 && (
        <Grid container spacing={3}>
          {/* Financing Mode Toggle - Compact version at top */}
          <Grid item xs={12}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              mb: 2,
              flexWrap: 'wrap',
              pb: 2,
              borderBottom: 1,
              borderColor: 'divider'
            }}>
              <Typography variant="h6">Financing Structure</Typography>

              {/* Financing Mode */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip
                  label="CapEx"
                  onClick={() => setFinancingMode('capex')}
                  color={financingMode === 'capex' ? 'primary' : 'default'}
                  variant={financingMode === 'capex' ? 'filled' : 'outlined'}
                  sx={{ fontWeight: financingMode === 'capex' ? 'bold' : 'normal', cursor: 'pointer' }}
                />
                <Chip
                  label="PPA/ESA"
                  onClick={() => setFinancingMode('ppa')}
                  color={financingMode === 'ppa' ? 'primary' : 'default'}
                  variant={financingMode === 'ppa' ? 'filled' : 'outlined'}
                  sx={{ fontWeight: financingMode === 'ppa' ? 'bold' : 'normal', cursor: 'pointer' }}
                />
              </Box>

              {/* ITC Toggle - Only show for CapEx mode */}
              {financingMode === 'capex' && (
                <>
                  <Divider orientation="vertical" flexItem />
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip
                      label="Include ITC"
                      onClick={() => setIncludeItc(true)}
                      color={includeItc ? 'success' : 'default'}
                      variant={includeItc ? 'filled' : 'outlined'}
                      size="small"
                      sx={{ fontWeight: includeItc ? 'bold' : 'normal', cursor: 'pointer' }}
                    />
                    <Chip
                      label="Exclude ITC"
                      onClick={() => setIncludeItc(false)}
                      color={!includeItc ? 'warning' : 'default'}
                      variant={!includeItc ? 'filled' : 'outlined'}
                      size="small"
                      sx={{ fontWeight: !includeItc ? 'bold' : 'normal', cursor: 'pointer' }}
                    />
                    <Typography variant="caption" color={includeItc ? 'success.main' : 'warning.main'} sx={{ ml: 1 }}>
                      {includeItc ? '30% ITC applied' : 'No tax incentives'}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </Grid>

          {/* PPA Pricing Parameters - Only show for PPA mode */}
          {financingMode === 'ppa' && (
            <Grid item xs={12}>
              <Card sx={{ backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#f8f9fa', mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontWeight: 600 }}>
                    PPA/ESA Pricing Parameters
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="Solar PPA Rate ($/kWh)"
                        type="number"
                        fullWidth
                        size="small"
                        value={ppaInputs.solarPpaRate || ''}
                        onChange={(e) => setPpaInputs(prev => ({ ...prev, solarPpaRate: parseFloat(e.target.value) || 0 }))}
                        inputProps={{ step: 0.01 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="Battery ESA Rate ($/kW-mo)"
                        type="number"
                        fullWidth
                        size="small"
                        value={ppaInputs.batteryEsaRate || ''}
                        onChange={(e) => setPpaInputs(prev => ({ ...prev, batteryEsaRate: parseFloat(e.target.value) || 0 }))}
                        inputProps={{ step: 1 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="Annual Escalator (%)"
                        type="number"
                        fullWidth
                        size="small"
                        value={ppaInputs.escalator || ''}
                        onChange={(e) => setPpaInputs(prev => ({ ...prev, escalator: parseFloat(e.target.value) || 0 }))}
                        inputProps={{ step: 0.1 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="Contract Term (years)"
                        type="number"
                        fullWidth
                        size="small"
                        value={ppaInputs.termYears || ''}
                        onChange={(e) => setPpaInputs(prev => ({ ...prev, termYears: parseInt(e.target.value) || 0 }))}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Key Financial Metrics - Prominent at top */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Key Financial Metrics
            </Typography>
            <Grid container spacing={2}>
              {updatedSummaryMetrics.map((metric, idx) => (
                <Grid item xs={12} sm={6} md={4} key={idx}>
                  <Card
                    elevation={3}
                    sx={{
                      height: '100%',
                      borderLeft: metric.label.includes('NPV') ? '4px solid' : 'none',
                      borderLeftColor: metric.label.includes('NPV') && activeFinancials.npv >= 0 ? 'success.main' : 'error.main'
                    }}
                  >
                    <CardContent>
                      <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                        {metric.label}
                      </Typography>
                      {/* Show skeleton while capex rates are loading to prevent number flickering */}
                      {capexRates.loading && capexSource === 'custom' ? (
                        <Skeleton variant="text" width="80%" height={40} />
                      ) : (
                        <Typography variant="h5" fontWeight="bold" sx={{
                          color: metric.label.includes('NPV')
                            ? (activeFinancials.npv >= 0 ? 'success.main' : 'error.main')
                            : 'text.primary'
                        }}>
                          {formatValue(metric.value, metric.type)}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>


          {/* System Configuration and Cost Summary - Side by Side */}
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                  System Configuration
                </Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Solar PV Size</TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" fontWeight="bold">
                          {formatNumber(systemData?.pvSize || 0, 0)} kW
                        </Typography>
                        {systemData?.pvPrescribed && systemData.pvPrescribed !== systemData.pvSize && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            Optimized from 0-{formatNumber(systemData.pvPrescribed, 0)} kW range
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Battery Storage</TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" fontWeight="bold">
                          {systemData?.storageSize > 0 || systemData?.storageCapacity > 0
                            ? `${formatNumber(systemData?.storageSize || 0, 0)} kW / ${formatNumber(systemData?.storageCapacity || 0, 0)} kWh`
                            : 'None'}
                        </Typography>
                        {systemData?.storagePrescribed && systemData.storagePrescribed !== systemData.storageSize && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            Optimized from 0-{formatNumber(systemData.storagePrescribed, 0)} kW / 0-{formatNumber(systemData.storageCapacityPrescribed, 0)} kWh range
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Generator</TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" fontWeight="bold">
                          {systemData?.generatorSize > 0
                            ? `${formatNumber(systemData?.generatorSize || 0, 0)} kW`
                            : 'None'}
                        </Typography>
                        {systemData?.generatorPrescribed && systemData.generatorPrescribed !== systemData.generatorSize && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            Optimized from 0-{formatNumber(systemData.generatorPrescribed, 0)} kW range
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                  Cost Summary
                </Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Upfront Capital Cost</TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" fontWeight="bold">
                          {formatCurrency(itcCalculations.totalUpfront)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ backgroundColor: isDark ? 'rgba(46, 125, 50, 0.1)' : 'rgba(46, 125, 50, 0.05)' }}>
                      <TableCell sx={{ fontWeight: 500 }}>
                        Investment Tax Credit ({itcCalculations.effectiveItcRate}%)
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" fontWeight="bold" color="success.main">
                          -{formatCurrency(itcCalculations.totalItc)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Net Cost After ITC</TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" fontWeight="bold" color="primary.main">
                          {formatCurrency(itcCalculations.netCostAfterItc)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Annual O&M Costs</TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" fontWeight="bold">
                          {formatCurrency(systemCosts.annualOmCosts)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          {/* BAU Baseline Metrics */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
              Business-as-Usual Baseline
              {summary.bauDataSource === 'backend_calculator' && (
                <Chip
                  label="Calculated from Tariff Data"
                  size="small"
                  color="info"
                  variant="outlined"
                  sx={{ ml: 2 }}
                />
              )}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={1} sx={{ height: '100%', backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#fafafa' }}>
                  <CardContent>
                    <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Year 1 Utility Cost
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }}>
                      {formatValue(summary.bauYearOneUtilityCost, 'currency')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={1} sx={{ height: '100%', backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#fafafa' }}>
                  <CardContent>
                    <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {customAssumptions.analysisYears}-Year Lifecycle Cost
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }}>
                      {formatValue(summary.bauLifecycleCost, 'currency')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={1} sx={{ height: '100%', backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#fafafa' }}>
                  <CardContent>
                    <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Average Utility Rate
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }}>
                      ${summary.bauUtilityRate ? summary.bauUtilityRate.toFixed(3) : '0.000'}/kWh
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={1} sx={{ height: '100%', backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#fafafa' }}>
                  <CardContent>
                    <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Total Savings vs BAU
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color={summary.totalSavings > 0 ? "success.main" : "error.main"} sx={{ mt: 1 }}>
                      {formatValue(summary.totalSavings, 'currency')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* Financial Assumptions - Read Only (configured in Commercial tab) */}
          <Grid item xs={12}>
            <Card sx={{ backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#f5f5f5' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Financial Assumptions
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  These values are configured in the <strong>Commercial</strong> tab and apply to all financial calculations.
                </Alert>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">Analysis Period</Typography>
                    <Typography variant="h6">{customAssumptions.analysisYears} years</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">Discount Rate</Typography>
                    <Typography variant="h6">{(customAssumptions.discountRate * 100).toFixed(1)}%</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )
      }

      {
        tab === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* KPI Summary Cards - Full width row */}
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Card elevation={2} sx={{ height: '100%', borderBottom: '3px solid #82ca9d' }}>
                  <CardContent sx={{ py: 1.5, px: 2 }}>
                    <Typography variant="caption" color="textSecondary">Project IRR</Typography>
                    <Typography variant="h5" fontWeight="bold" color="success.main">
                      {(() => {
                        const irr = calculateIRR(filteredCashflows, summary.initialInvestment || 0);
                        return irr !== null ? `${(irr * 100).toFixed(1)}%` : '—';
                      })()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card elevation={2} sx={{ height: '100%', borderBottom: '3px solid #8884d8' }}>
                  <CardContent sx={{ py: 1.5, px: 2 }}>
                    <Typography variant="caption" color="textSecondary">Net Present Value</Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: summary.npv >= 0 ? 'success.main' : 'error.main' }}>
                      {formatCurrency(summary.npv)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card elevation={2} sx={{ height: '100%', borderBottom: '3px solid #ffc658' }}>
                  <CardContent sx={{ py: 1.5, px: 2 }}>
                    <Typography variant="caption" color="textSecondary">Payback Period</Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {summary.paybackPeriod ? `${summary.paybackPeriod.toFixed(1)} yrs` : '—'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card elevation={2} sx={{ height: '100%', borderBottom: '3px solid #ff7c7c' }}>
                  <CardContent sx={{ py: 1.5, px: 2 }}>
                    <Typography variant="caption" color="textSecondary">Total Lifecycle Savings</Typography>
                    <Typography variant="h5" fontWeight="bold" color="success.main">
                      {formatCurrency(summary.totalSavings)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Charts Row */}
            <Grid container spacing={3}>
              {/* Annual Cashflow Chart */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Annual Cash Flow</Typography>
                    <Box sx={{ height: 350, width: '100%' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={cashflowChartData}>
                          <XAxis dataKey="year" />
                          <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Legend />
                          <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />
                          <Bar dataKey="energySavings" fill="#82ca9d" name="Energy Savings" />
                          <Bar dataKey="omCost" fill="#ff7c7c" name="O&M Costs" />
                          <Bar dataKey="fuelCost" fill="#ffb347" name="Fuel Costs" />
                          <Bar dataKey="standbyCharges" fill="#ff9800" name="Standby Charges" />
                          {financingMode === 'ppa' && (
                            <Bar dataKey="ppaCost" fill="#ffa726" name="PPA/ESA Costs" />
                          )}
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>


              {/* Cumulative Cashflow Chart - Made taller with reference line */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Cumulative Cash Flow and Payback</Typography>
                    <Box sx={{ height: 350, width: '100%' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={[
                          { year: 0, cumulativeNetCashflow: -(summary.initialInvestment || 0) },
                          ...cashflowChartData
                        ]}>
                          <XAxis dataKey="year" />
                          <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Legend />
                          <ReferenceLine y={0} stroke="#82ca9d" strokeWidth={2} label={{ value: 'Payback', fill: '#82ca9d', fontSize: 12, position: 'insideTopRight' }} />
                          <Line
                            type="monotone"
                            dataKey="cumulativeNetCashflow"
                            stroke="#8884d8"
                            name="Cumulative Net Cash Flow"
                            strokeWidth={3}
                            dot={{ r: 4 }}
                            activeDot={{ r: 6 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Enhanced Cashflow Table with Year 0 */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">Detailed Cash Flow Analysis</Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => exportCashFlowToCSV(
                          filteredCashflows,
                          summary.initialInvestment || 0,
                          includeItc ? (itcCalculations?.totalItc || 0) : 0
                        )}
                      >
                        Export CSV
                      </Button>
                    </Box>
                    <Box sx={{ maxHeight: 450, overflowX: 'auto', overflowY: 'auto' }}>
                      <Table size="small" sx={{ minWidth: 900 }}>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#f5f5f5' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>Year</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>BAU Utility Cost</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Project Utility Cost</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Energy Savings</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>O&M Costs</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Fuel Costs</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', color: '#ff9800' }}>Standby Charges</TableCell>
                            {summary.taxIncentives?.includeMacrs && (
                              <TableCell align="right" sx={{ fontWeight: 'bold', color: '#4caf50' }}>MACRS Tax Shield</TableCell>
                            )}
                            {(summary.totalItcCashflow || 0) > 0 && (
                              <TableCell align="right" sx={{ fontWeight: 'bold', color: '#4caf50' }}>ITC Credit</TableCell>
                            )}
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Net Cash Flow</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Cumulative</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>PV Production</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {/* Year 0 - Initial Investment */}
                          <TableRow sx={{ backgroundColor: isDark ? 'rgba(255, 200, 100, 0.1)' : '#fffde7' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>0</TableCell>
                            <TableCell align="right">—</TableCell>
                            <TableCell align="right">—</TableCell>
                            <TableCell align="right">—</TableCell>
                            <TableCell align="right">—</TableCell>
                            <TableCell align="right">—</TableCell>
                            <TableCell align="right">—</TableCell>
                            {summary.taxIncentives?.includeMacrs && (
                              <TableCell align="right">—</TableCell>
                            )}
                            {(summary.totalItcCashflow || 0) > 0 && (
                              <TableCell align="right">—</TableCell>
                            )}
                            <TableCell align="right" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                              {formatCurrency(-(summary.initialInvestment || 0))}
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                              {formatCurrency(-(summary.initialInvestment || 0))}
                            </TableCell>
                            <TableCell align="right">—</TableCell>
                          </TableRow>
                          {/* Years 1-N */}
                          {filteredCashflows.map((cf) => {
                            const cumulativeVal = cashflowChartData.find(d => d.year === cf.year)?.cumulativeNetCashflow || 0;
                            const isPaybackYear = cumulativeVal >= 0 && cashflowChartData.find(d => d.year === cf.year - 1)?.cumulativeNetCashflow < 0;
                            return (
                              <TableRow
                                key={cf.year}
                                sx={{
                                  backgroundColor: isPaybackYear
                                    ? (isDark ? 'rgba(130, 202, 157, 0.15)' : '#e8f5e9')
                                    : 'inherit'
                                }}
                              >
                                <TableCell sx={{ fontWeight: isPaybackYear ? 'bold' : 'normal' }}>
                                  {cf.year}
                                  {isPaybackYear && <Typography component="span" variant="caption" color="success.main" sx={{ ml: 1 }}>✓ Payback</Typography>}
                                </TableCell>
                                <TableCell align="right">{formatCurrency(cf.bauUtilityCost)}</TableCell>
                                <TableCell align="right">{formatCurrency(cf.projectUtilityCost)}</TableCell>
                                <TableCell align="right" sx={{ color: 'success.main' }}>{formatCurrency(cf.energySavings)}</TableCell>
                                <TableCell align="right" sx={{ color: cf.omCost > 0 ? 'error.main' : 'inherit' }}>{formatCurrency(cf.omCost)}</TableCell>
                                <TableCell align="right" sx={{ color: cf.fuelCost > 0 ? 'error.main' : 'inherit' }}>{formatCurrency(cf.fuelCost || 0)}</TableCell>
                                <TableCell align="right" sx={{ color: (cf.standbyCharges || 0) > 0 ? '#ff9800' : 'inherit' }}>{formatCurrency(cf.standbyCharges || 0)}</TableCell>
                                {summary.taxIncentives?.includeMacrs && (
                                  <TableCell align="right" sx={{ color: (cf.depreciationTaxShield || 0) > 0 ? '#4caf50' : 'inherit' }}>
                                    {(cf.depreciationTaxShield || 0) > 0 ? formatCurrency(cf.depreciationTaxShield) : '—'}
                                  </TableCell>
                                )}
                                {(summary.totalItcCashflow || 0) > 0 && (
                                  <TableCell align="right" sx={{ color: (cf.itcCashflow || 0) > 0 ? '#4caf50' : 'inherit' }}>
                                    {(cf.itcCashflow || 0) > 0 ? formatCurrency(cf.itcCashflow) : '—'}
                                  </TableCell>
                                )}
                                <TableCell align="right" sx={{ fontWeight: 500 }}>{formatCurrency(cf.netCashflow)}</TableCell>
                                <TableCell align="right" sx={{
                                  color: cumulativeVal >= 0 ? 'success.main' : 'error.main',
                                  fontWeight: 500
                                }}>
                                  {formatCurrency(cumulativeVal)}
                                </TableCell>
                                <TableCell align="right">{formatNumber(cf.pvProduction)} kWh</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )
      }


      {
        tab === 2 && (
          <>
            <BudgetTab
              systemSizes={systemSizes || {}}
              budget={budget}
              hasSystemConfigured={true}
              readOnly={true}
            />
            <Box sx={{ mt: 3 }}>
              <OpExTable
                systemSizes={systemSizes || {}}
                budget={budget}
                hasSystemConfigured={true}
                readOnly={true}
              />
            </Box>
          </>
        )
      }




    </Box >
  );
}